import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

const SIZE_STYLES = {
  sm: {
    trigger: "h-9 rounded-lg border-gray-300 px-3 text-xs",
    item: "text-xs",
    search: "text-xs",
  },
  md: {
    trigger: "min-h-12 rounded-xl border-gray-200 p-3 text-sm",
    item: "text-sm",
    search: "text-sm",
  },
};

const normalizeOption = (option) =>
  typeof option === "string"
    ? { value: option, label: option }
    : { value: String(option?.value ?? ""), label: String(option?.label ?? option?.value ?? "") };

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  pinnedOption,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyLabel = "No results found",
  className = "",
  disabled = false,
  size = "sm",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();
  const styles = SIZE_STYLES[size] || SIZE_STYLES.sm;

  const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
  const pinned = pinnedOption === undefined
    ? null
    : normalizedOptions.find((option) => option.value === pinnedOption) || null;

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rest = pinned ? normalizedOptions.filter((option) => option !== pinned) : normalizedOptions;
    const matches = needle ? rest.filter((option) => option.label.toLowerCase().includes(needle)) : rest;
    return pinned ? [pinned, ...matches] : matches;
  }, [normalizedOptions, pinned, query]);

  const hasMatches = filteredOptions.some((option) => option !== pinned);
  const selectedOption = value === "" || value === undefined || value === null
    ? null
    : normalizedOptions.find((option) => option.value === String(value)) || null;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", handlePointer);
    searchRef.current?.focus();
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const item = listRef.current?.children?.[highlightIndex];
    item?.scrollIntoView?.({ block: "nearest" });
  }, [highlightIndex, open]);

  const openMenu = () => {
    if (disabled) return;
    setQuery("");
    const selectedIndex = filteredOptions.findIndex((option) => option.value === String(value ?? ""));
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const selectOption = (option) => {
    onChange(option.value);
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[highlightIndex];
      if (option) selectOption(option);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className={`w-full border bg-white text-gray-800 inline-flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60 ${styles.trigger}`}
      >
        <span className={`truncate ${selectedOption ? "" : "text-gray-400"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={14} className="shrink-0 text-gray-500" />
      </button>

      {open ? (
        <div className="absolute left-0 z-50 mt-1 w-full min-w-[220px] rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="relative border-b border-gray-100 p-2">
            <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              aria-controls={listId}
              className={`h-8 w-full rounded-md border border-gray-200 pl-7 pr-2 text-gray-800 outline-none focus:border-gray-400 ${styles.search}`}
            />
          </div>

          <ul ref={listRef} id={listId} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filteredOptions.map((option, index) => {
              const selected = selectedOption?.value === option.value;
              const highlighted = index === highlightIndex;
              return (
                <li
                  key={option.value || "__empty"}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                  className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 ${styles.item} ${
                    highlighted ? "bg-gray-100" : ""
                  } ${selected ? "font-bold text-gray-900" : "text-gray-700"}`}
                >
                  <span className="truncate">{option.label}</span>
                  {selected ? <Check size={13} className="shrink-0 text-[#8b0000]" /> : null}
                </li>
              );
            })}
          </ul>

          {!hasMatches && query.trim() ? (
            <p className="px-3 pb-3 pt-1 text-xs text-gray-400">{emptyLabel}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SearchableSelect;
